import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { CONTENT_PAGE_HEADERS } from "~/lib/cache.server"
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { blogPosts } from '@afficheservices/db/schema'
import { eq, desc } from 'drizzle-orm'
import { marked } from 'marked'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { ArticleSchema, ArticleMeta } from '~/components/seo-content'
import { ShareButtons } from '~/components/share-buttons'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { AuthorByline, AuthorSchema, AUTHORS } from '~/components/eeat-author'
import { SourceCitations } from '~/components/content-trust'
import { BlogTopicEnrichment } from '~/components/blog-topic-enrichment'

/**
 * Map blog post tags/slug/title to a service category for SourceCitations
 * lookup. Returns null when no clear topic match — block is skipped to avoid
 * injecting irrelevant authority links.
 */
const TOPIC_KEYWORDS: Array<{ slug: string; words: string[] }> = [
  { slug: 'plomberie', words: ['plombier', 'plomberie', 'fuite', 'sanitaire', 'chauffe-eau', 'robinet', 'douche', 'wc', 'évacuation'] },
  { slug: 'electricite', words: ['électricien', 'electricite', 'électricité', 'tableau électrique', 'consuel', 'prise', 'disjoncteur', 'borne de recharge'] },
  { slug: 'peinture', words: ['peintre', 'peinture', 'enduit', 'sous-couche'] },
  { slug: 'renovation', words: ['rénovation', 'rénover', 'maprimerenov', 'salle de bain', 'cuisine'] },
  { slug: 'menuiserie', words: ['menuisier', 'menuiserie', 'parquet', 'fenêtre', 'porte', 'placard', 'bibliothèque'] },
  { slug: 'toiture', words: ['toiture', 'couvreur', 'tuile', 'ardoise', 'zinguerie'] },
  { slug: 'serrurerie', words: ['serrurier', 'serrure', 'cylindre', 'verrou', 'a2p'] },
  { slug: 'climatisation', words: ['climatisation', 'pompe à chaleur', 'pac', 'clim', 'chauffage'] },
]

function detectBlogTopic(slug: string, title: string, tags?: string[] | null): string | null {
  const haystack = [slug, title, ...(tags ?? [])].join(' ').toLowerCase()
  for (const { slug: s, words } of TOPIC_KEYWORDS) {
    if (words.some((w) => haystack.includes(w))) return s
  }
  return null
}

// Fallback articles for when DB has no content yet
const FALLBACK_ARTICLES: Record<string, { title: string; category: string; date: string; readTime: string; image: string; content: string }> = {
  'comment-choisir-son-plombier': {
    title: 'Comment choisir un bon plombier ? 7 critères essentiels',
    category: 'Conseils', date: '15 mars 2026', readTime: '5 min',
    image: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=1200&q=80',
    content: `Trouver un plombier de confiance est essentiel pour éviter les mauvaises surprises. Voici les 7 critères à vérifier.\n\n## 1. Vérifiez les qualifications\nUn bon plombier doit posséder un diplôme reconnu : CAP Installateur sanitaire, BEP ou Bac Pro.\n\n## 2. Contrôlez l'assurance\nExigez une attestation d'assurance RC Professionnelle en cours de validité. La garantie décennale est obligatoire pour les gros travaux.\n\n## 3. Demandez un devis détaillé\nUn devis écrit est obligatoire pour tout travail dépassant 150 €. Il doit mentionner la nature des travaux, les matériaux, le prix TTC et la durée.\n\n## 4. Consultez les avis clients\nVérifiez les avis sur des plateformes fiables. Sur AfficheServices, tous les avis sont vérifiés et modérés.\n\n## 5. Comparez les prix\nDemandez au minimum 3 devis pour le même travail. Les écarts peuvent atteindre 40%.\n\n## 6. Vérifiez le SIRET\nUn artisan sérieux a un numéro SIRET valide. Vérifiez-le sur societe.com.\n\n## 7. Privilégiez la proximité\nUn artisan proche de chez vous réduit les frais de déplacement et intervient plus rapidement.`,
  },
  'renovation-salle-de-bain-guide-complet': {
    title: 'Rénovation salle de bain : le guide complet 2026',
    category: 'Guides', date: '12 mars 2026', readTime: '8 min',
    image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=1200&q=80',
    content: `De la planification au choix des matériaux, tout ce qu'il faut savoir pour réussir la rénovation de votre salle de bain.\n\n## Budget\nRafraîchissement : 3 000–5 000 €. Rénovation complète : 5 000–15 000 €. Haut de gamme : 15 000–25 000 €.\n\n## Étapes clés\n1. Diagnostic de l'existant\n2. Plan et choix des matériaux\n3. Plomberie et électricité\n4. Revêtements (carrelage, peinture)\n5. Installation des sanitaires\n6. Finitions\n\n## Conseils\n- Prévoyez 10-15% du budget pour les imprévus\n- L'étanchéité est le poste à ne pas négliger\n- Un bon éclairage transforme une salle de bain moyenne en espace premium`,
  },
}

export async function loader({ params }: LoaderFunctionArgs) {
  const { slug } = params

  // Try DB first
  const [dbPost] = await db
    .select({
      slug: blogPosts.slug,
      title: blogPosts.titleFr,
      content: blogPosts.contentFr,
      excerpt: blogPosts.excerptFr,
      image: blogPosts.coverImageUrl,
      tags: blogPosts.tags,
      seoTitle: blogPosts.seoTitle,
      seoDescription: blogPosts.seoDescription,
      publishedAt: blogPosts.publishedAt,
      createdAt: blogPosts.createdAt,
      updatedAt: blogPosts.updatedAt,
    })
    .from(blogPosts)
    .where(eq(blogPosts.slug, slug ?? ''))
    .limit(1)

  // Fetch related articles (same tag or recent)
  const relatedPosts = await db
    .select({ slug: blogPosts.slug, title: blogPosts.titleFr, image: blogPosts.coverImageUrl, tags: blogPosts.tags })
    .from(blogPosts)
    .where(eq(blogPosts.status, 'published'))
    .orderBy(desc(blogPosts.publishedAt))
    .limit(20)

  if (dbPost) {
    const related = relatedPosts
      .filter((p) => p.slug !== dbPost.slug)
      .sort((a, b) => {
        const aMatch = a.tags?.some((t) => dbPost.tags?.includes(t)) ? 1 : 0
        const bMatch = b.tags?.some((t) => dbPost.tags?.includes(t)) ? 1 : 0
        return bMatch - aMatch
      })
      .slice(0, 3)

    // Word count drives quality signals (ArticleSchema wordCount + AI weight).
    const wordCount = (dbPost.content.match(/\S+/g)?.length) ?? 0
    return {
      source: 'db' as const,
      slug: dbPost.slug,
      title: dbPost.title,
      content: dbPost.content,
      excerpt: dbPost.excerpt ?? '',
      image: dbPost.image ?? 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80',
      category: dbPost.tags?.[0] ?? 'Conseils',
      tags: dbPost.tags,
      date: (dbPost.publishedAt ?? dbPost.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }),
      datePublishedIso: (dbPost.publishedAt ?? dbPost.createdAt).toISOString(),
      dateModifiedIso: (dbPost.updatedAt ?? dbPost.publishedAt ?? dbPost.createdAt).toISOString(),
      readTime: `${Math.max(3, Math.round(dbPost.content.length / 1000))} min`,
      wordCount,
      seoTitle: dbPost.seoTitle,
      seoDescription: dbPost.seoDescription,
      related,
      ...renderArticleHtml(dbPost.content),
    }
  }

  // Fallback to hardcoded
  const fallback = FALLBACK_ARTICLES[slug ?? '']
  if (!fallback) throw new Response('Article non trouvé', { status: 404 })

  const fallbackWordCount = (fallback.content.match(/\S+/g)?.length) ?? 0
  return {
    source: 'fallback' as const,
    slug: slug ?? '',
    tags: null as string[] | null,
    ...fallback,
    excerpt: fallback.content.slice(0, 160),
    datePublishedIso: new Date().toISOString(),
    dateModifiedIso: new Date().toISOString(),
    wordCount: fallbackWordCount,
    seoTitle: null,
    seoDescription: null,
    related: relatedPosts.filter((p) => p.slug !== slug).slice(0, 3),
    ...renderArticleHtml(fallback.content),
  }
}

export function headers() { return CONTENT_PAGE_HEADERS }

export const meta: MetaFunction<typeof loader> = ({ data }) => [
  // Drop any trailing " | AfficheServices" — Google appends the brand itself,
  // and the suffix pushed post titles past 60 chars (DataForSEO: title_too_long).
  { title: (data?.seoTitle ?? data?.title ?? 'Article').replace(/\s*\|\s*AfficheServices\s*$/, '') },
  { name: 'description', content: data?.seoDescription ?? data?.excerpt },
  { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/blog/${data?.slug}` },
  { property: 'og:title', content: data?.title ?? 'Article' },
  { property: 'og:description', content: data?.excerpt ?? '' },
  { property: 'og:image', content: data?.image ?? 'https://www.afficheservices.fr/og-default.png' },
  { property: 'og:url', content: `https://www.afficheservices.fr/blog/${data?.slug}` },
  { property: 'og:type', content: 'article' },
]

// Minimal HTML sanitizer for marked output. We don't pull in DOMPurify
// (browser-only) or sanitize-html (extra dep) — instead we strip the
// concrete attack vectors that marked passes through from raw HTML in
// markdown source. Defence-in-depth even though blog content is admin-only
// today (audit #5: a single compromised admin account or a future user
// comment feature would be stored XSS otherwise).
function sanitizeMarkedHtml(html: string): string {
  return html
    // Drop entire <script>, <iframe>, <object>, <embed>, <style> blocks
    .replace(/<(script|iframe|object|embed|style)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
    // Drop self-closing dangerous tags
    .replace(/<(script|iframe|object|embed|style|link|meta)\b[^>]*\/?>/gi, '')
    // Strip on*= event handler attributes (onload, onclick, etc.)
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    // Neutralize javascript: / data:text/html / vbscript: URI schemes in href/src
    .replace(/(href|src)\s*=\s*("|')\s*(javascript|vbscript|data:text\/html)[^"']*\2/gi, '$1=$2#$2')
}

/**
 * Render markdown → sanitized HTML with heading anchors. Runs ONLY in the
 * loader (server) so the `marked` parser (~30-40 KB) never ships to the client
 * bundle — the component just receives the finished HTML string.
 */
function renderArticleHtml(content: string): { html: string; headings: Array<{ text: string; id: string }> } {
  const normalized = content.replace(/\\n/g, '\n')
  const rawHtml = marked.parse(normalized, { breaks: true, gfm: true }) as string
  let html = sanitizeMarkedHtml(rawHtml)
  const headings = content.match(/^##\s+(.+)$/gm)?.map((h) => {
    const text = h.replace(/^##\s+/, '')
    // Prefix with `h-` to avoid DOM clobbering on identifiers like __proto__
    const id = 'h-' + text.toLowerCase().replace(/[^a-zà-ÿ0-9]+/gi, '-').replace(/^-|-$/g, '')
    return { text, id }
  }) ?? []
  for (const h of headings) {
    html = html.replace(`<h2>${h.text}</h2>`, `<h2 id="${h.id}">${h.text}</h2>`)
  }
  return { html, headings }
}

export default function BlogPost() {
  const data = useLoaderData<typeof loader>()

  // HTML + heading anchors are rendered server-side in the loader
  // (renderArticleHtml) so `marked` never ships to the client.
  const processedHtml = data.html
  const headings = data.headings

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Blog', url: 'https://www.afficheservices.fr/blog' },
        { name: data.title, url: `https://www.afficheservices.fr/blog/${data.slug}` },
      ]} />
      <ArticleSchema
        title={data.title}
        description={data.excerpt}
        url={`https://www.afficheservices.fr/blog/${data.slug}`}
        datePublished={data.datePublishedIso}
        dateModified={data.dateModifiedIso}
        image={data.image}
        keywords={data.tags}
        articleSection={data.category}
        wordCount={data.wordCount}
      />
      <AuthorSchema author={AUTHORS['equipe-editoriale']} articleUrl={`https://www.afficheservices.fr/blog/${data.slug}`} />

      <article className="mx-auto max-w-3xl px-4 py-12 lg:px-6">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
          <ArrowLeft className="h-3.5 w-3.5" /> Tous les articles
        </Link>

        <div className="mt-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary">{data.category}</span>
            {/* Visible "Mis à jour le" badge — E-E-A-T freshness signal +
                trust cue для visitors and crawlers. */}
            <time
              dateTime={data.dateModifiedIso}
              className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700"
            >
              Mis à jour le {new Date(data.dateModifiedIso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </time>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
              {data.wordCount.toLocaleString('fr-FR')} mots
            </span>
          </div>
          <h1 className="mt-4 text-3xl font-bold text-slate-900">{data.title}</h1>
          <div className="mt-3">
            <ArticleMeta readingTime={parseInt(data.readTime)} />
          </div>
          <div className="mt-4">
            <AuthorByline author={AUTHORS['equipe-editoriale']} readTime={parseInt(data.readTime)} />
          </div>
        </div>

        {data.image && (
          <img
            src={data.image}
            alt={data.title}
            className="mt-6 w-full rounded-2xl object-cover"
            style={{ maxHeight: 400 }}
            loading="eager"
            onError={(e) => {
              const img = e.currentTarget
              if (!img.src.includes('photo-1600585154340')) {
                img.src = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80'
              } else {
                img.style.display = 'none'
              }
            }}
          />
        )}

        {/* Key Takeaways — AI/SGE/Perplexity lift target. Short, declarative,
            citation-friendly. Combines excerpt + top 3 article sections.
            Class `key-takeaways` matches ArticleSchema speakable selector. */}
        {(data.excerpt || headings.length >= 2) && (
          <aside className="key-takeaways mt-8 rounded-2xl border border-amber-200 bg-amber-50/50 p-6">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-700">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white">✓</span>
              À retenir
            </p>
            {data.excerpt && (
              <p className="mt-3 text-sm leading-relaxed text-slate-700">{data.excerpt}</p>
            )}
            {headings.length >= 2 && (
              <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
                {headings.slice(0, 4).map((h) => (
                  <li key={h.id} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" />
                    <span>{h.text}</span>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        )}

        {/* Table of Contents */}
        {headings.length >= 3 && (
          <nav className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-5" aria-label="Sommaire">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Dans cet article</p>
            <ul className="mt-3 space-y-2">
              {headings.map((h) => (
                <li key={h.id}>
                  <a href={`#${h.id}`} className="text-sm text-slate-600 hover:text-primary hover:underline">{h.text}</a>
                </li>
              ))}
            </ul>
          </nav>
        )}

        {/* Content rendered from markdown — source: admin-created blog posts in DB, not user input */}
        <div
          className="prose prose-slate prose-sm mt-8 max-w-none prose-headings:font-bold prose-h2:text-xl prose-h2:mt-8 prose-h3:text-lg prose-h3:mt-6 prose-p:leading-relaxed prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-table:text-sm prose-th:bg-slate-50 prose-th:px-3 prose-th:py-2 prose-td:px-3 prose-td:py-2 prose-td:border-t prose-td:border-slate-100 prose-strong:text-slate-900 prose-li:text-slate-600 prose-ul:mt-3 prose-ol:mt-3"
          dangerouslySetInnerHTML={{ __html: processedHtml }}
        />

        {/* Share */}
        <div className="mt-8 flex items-center justify-between border-t border-border pt-6">
          <ShareButtons url={`/blog/${data.slug}`} title={data.title} />
        </div>

        {/* Topical authority — official sources, AI citation booster (#2) */}
        {(() => {
          const topic = detectBlogTopic(data.slug, data.title, data.tags)
          if (!topic) return null
          return (
            <>
              <SourceCitations serviceSlug={topic} />
              {/* Pour aller plus loin — internal-link block, depth-builder (#5) */}
              <div className="mt-6 rounded-2xl border border-border bg-white p-6 shadow-xs">
                <h2 className="text-lg font-bold text-slate-900">Pour aller plus loin</h2>
                <p className="mt-2 text-sm text-slate-500">
                  Approfondissez le sujet avec nos guides dédiés et nos calculateurs de prix.
                </p>
                <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                  <li>
                    <Link to={`/services/${topic}`} className="block rounded-lg border border-slate-200 p-3 transition-colors hover:border-primary hover:bg-primary-50">
                      <span className="text-sm font-semibold text-slate-900">→ Trouver un artisan {topic.replace(/-/g, ' ')}</span>
                      <p className="mt-0.5 text-xs text-slate-500">Artisans vérifiés près de chez vous</p>
                    </Link>
                  </li>
                  <li>
                    <Link to={`/guide-prix/${topic}`} className="block rounded-lg border border-slate-200 p-3 transition-colors hover:border-primary hover:bg-primary-50">
                      <span className="text-sm font-semibold text-slate-900">→ Guide des prix {topic.replace(/-/g, ' ')}</span>
                      <p className="mt-0.5 text-xs text-slate-500">Tarifs détaillés et fourchettes 2026</p>
                    </Link>
                  </li>
                  <li>
                    <Link to="/cee" className="block rounded-lg border border-slate-200 p-3 transition-colors hover:border-primary hover:bg-primary-50">
                      <span className="text-sm font-semibold text-slate-900">→ Aides CEE et MaPrimeRénov'</span>
                      <p className="mt-0.5 text-xs text-slate-500">Économies jusqu'à 20 000 €</p>
                    </Link>
                  </li>
                  <li>
                    <Link to="/devis-gratuit" className="block rounded-lg border border-slate-200 p-3 transition-colors hover:border-primary hover:bg-primary-50">
                      <span className="text-sm font-semibold text-slate-900">→ Demander 3 devis gratuits</span>
                      <p className="mt-0.5 text-xs text-slate-500">Réponse sous 48h, sans engagement</p>
                    </Link>
                  </li>
                </ul>
              </div>
              {/* Topic enrichment — adds ~500-700 words of structured content
                  (FAQ + prices + lexicon + related guides) per post. Lifts thin
                  blog posts toward the 1500w content quality floor without
                  per-post manual authoring. */}
              <BlogTopicEnrichment topic={topic as Parameters<typeof BlogTopicEnrichment>[0]['topic']} />
            </>
          )
        })()}

        {/* CTA */}
        <div className="mt-8 rounded-2xl bg-primary-50 p-8 text-center">
          <h3 className="text-lg font-bold text-slate-900">Besoin d'un artisan ?</h3>
          <p className="mt-2 text-sm text-slate-500">Recevez 3 devis gratuits de professionnels vérifiés</p>
          <Link to="/demande-devis/1" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary-hover btn-press">
            Demander un devis <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </article>

      {/* Related articles */}
      {data.related && data.related.length > 0 && (
        <section className="border-t border-border bg-surface-secondary py-12">
          <div className="mx-auto max-w-7xl px-4 lg:px-6">
            <h2 className="text-xl font-bold text-slate-900">Articles similaires</h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-3">
              {data.related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="card-hover group overflow-hidden rounded-xl border border-border bg-white shadow-xs">
                  {r.image && (
                    <img
                      src={r.image}
                      alt={r.title}
                      className="h-36 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                      onError={(e) => {
                        const img = e.currentTarget
                        if (!img.src.includes('photo-1600585154340')) {
                          img.src = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800'
                        } else {
                          img.style.display = 'none'
                        }
                      }}
                    />
                  )}
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-slate-900 group-hover:text-primary line-clamp-2">{r.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  )
}
